import React, { useContext, useEffect, useState, useRef, useCallback } from "react";
import {
  Container,
  TextField,
  Box,
  Alert,
  Typography,
  FormControl,
  InputLabel,
  OutlinedInput,
  IconButton,
  InputAdornment,
  Paper,
  LinearProgress,
  Tooltip,
  Chip,
} from "@mui/material";
import { keyframes, styled } from "@mui/system";
import { LoadingButton } from "@mui/lab";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Visibility, VisibilityOff, Refresh, Lock, Security } from "@mui/icons-material";
import { Settings, Wrench, Factory, Cpu, Hammer, Cog, ShieldCheck, ShieldAlert } from "lucide-react";
import { getDbHeader, getErrorResponse } from "../utils/util";
import axios from "axios";
import { API_ENDPOINT } from "../utils/constants";
import { saveToken } from "../utils/utils2";
import { UserContext } from "../context/UserProvider";
import MuPb from "../widgets/MuPb";

// 1. Floating Animation Keyframes
const float = keyframes`
  0% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-30px) rotate(15deg); }
  100% { transform: translateY(0px) rotate(0deg); }
`;

const floatReverse = keyframes`
  0% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(30px) rotate(-15deg); }
  100% { transform: translateY(0px) rotate(0deg); }
`;

const FloatingIconBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "anim" && prop !== "delay",
})(({ anim, delay }) => ({
  position: "absolute",
  opacity: 0.12,
  color: "inherit",
  zIndex: 0,
  pointerEvents: "none",
  animation: `${anim} 10s ease-in-out infinite`,
  animationDelay: delay,
  "@media (prefers-reduced-motion: reduce)": {
    animation: `${anim} 10s ease-in-out infinite !important`,
  },
}));

const FloatingIcon = ({ Icon, top, left, right, bottom, size, animation, delay }) => (
  <FloatingIconBox anim={animation} delay={delay} sx={{ top, left, right, bottom, color: "primary.main" }}>
    <Icon size={size} strokeWidth={1} />
  </FloatingIconBox>
);

// Helper: Generate Random Alphanumeric Code for Captcha
const generateRandomCaptcha = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let code = "";
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

// Helper: Calculate Password Strength (0-100)
const getPasswordStrength = (pass) => {
  if (!pass) return { score: 0, label: "", color: "transparent" };
  let score = 0;
  if (pass.length >= 6) score += 25;
  if (pass.length >= 10) score += 25;
  if (/[A-Z]/.test(pass) && /[a-z]/.test(pass)) score += 25;
  if (/[0-9]/.test(pass) || /[^A-Za-z0-9]/.test(pass)) score += 25;

  if (score <= 25) return { score, label: "Weak", color: "#EF4444" };
  if (score <= 50) return { score, label: "Medium", color: "#F59E0B" };
  if (score <= 75) return { score, label: "Good", color: "#3B82F6" };
  return { score, label: "Strong & Secure", color: "#10B981" };
};

function Login() {
  const navigate = useNavigate();
  const { user, loading: loadingUser, reloadUser } = useContext(UserContext);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Captcha & Security States
  const [captchaCode, setCaptchaCode] = useState("");
  const [captchaInput, setCaptchaInput] = useState("");
  const [captchaError, setCaptchaError] = useState(false);
  const canvasRef = useRef(null);

  // Rate Limiting / Brute-force Lock States
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [lockCountdown, setLockCountdown] = useState(0);

  // Function to draw CAPTCHA onto Canvas
  const drawCaptcha = useCallback((code) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const width = canvas.width;
    const height = canvas.height;

    // Background gradient
    const grad = ctx.createLinearGradient(0, 0, width, height);
    grad.addColorStop(0, "#F1F5F9");
    grad.addColorStop(1, "#E2E8F0");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, width, height);

    // Random Background Noise Lines
    for (let i = 0; i < 5; i++) {
      ctx.strokeStyle = `rgba(${Math.random() * 100}, ${Math.random() * 100}, ${Math.random() * 100}, 0.25)`;
      ctx.lineWidth = Math.random() * 2 + 1;
      ctx.beginPath();
      ctx.moveTo(Math.random() * width, Math.random() * height);
      ctx.lineTo(Math.random() * width, Math.random() * height);
      ctx.stroke();
    }

    // Random Dots
    for (let i = 0; i < 30; i++) {
      ctx.fillStyle = `rgba(0, 0, 0, ${Math.random() * 0.3})`;
      ctx.beginPath();
      ctx.arc(Math.random() * width, Math.random() * height, Math.random() * 2, 0, Math.PI * 2);
      ctx.fill();
    }

    // Draw Captcha Text with rotation
    ctx.font = "bold 22px 'Courier New', monospace";
    ctx.textBaseline = "middle";

    for (let i = 0; i < code.length; i++) {
      const char = code[i];
      const x = 18 + i * 22;
      const y = height / 2 + (Math.random() * 4 - 2);

      ctx.save();
      ctx.translate(x, y);
      ctx.rotate((Math.random() - 0.5) * 0.3); // Slight tilt

      ctx.fillStyle = `hsl(${Math.random() * 200 + 180}, 70%, 25%)`;
      ctx.fillText(char, 0, 0);
      ctx.restore();
    }
  }, []);

  const refreshCaptcha = useCallback(() => {
    const newCode = generateRandomCaptcha();
    setCaptchaCode(newCode);
    setCaptchaInput("");
    setCaptchaError(false);
    setTimeout(() => drawCaptcha(newCode), 50);
  }, [drawCaptcha]);

  useEffect(() => {
    refreshCaptcha();
  }, [refreshCaptcha]);

  useEffect(() => {
    if (!loadingUser && user) {
      navigate("/", { replace: true });
    }
  }, [user, loadingUser, navigate]);

  // Lock countdown timer interval
  useEffect(() => {
    if (lockCountdown <= 0) return;
    const timer = setInterval(() => {
      setLockCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setFailedAttempts(0);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [lockCountdown]);

  if (loadingUser) {
    return <MuPb />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Lock check
    if (lockCountdown > 0) {
      toast.error(`Account locked. Please wait ${lockCountdown} seconds.`);
      return;
    }

    // Input validations
    const cleanUsername = username.trim();
    if (!cleanUsername) {
      toast.error("Username is required");
      return;
    }
    if (!password) {
      toast.error("Password is required");
      return;
    }

    // Captcha validation
    if (captchaInput.trim().toUpperCase() !== captchaCode.toUpperCase()) {
      setCaptchaError(true);
      toast.error("Invalid CAPTCHA code. Please try again.");
      refreshCaptcha();

      const newFailed = failedAttempts + 1;
      setFailedAttempts(newFailed);
      if (newFailed >= 5) {
        setLockCountdown(30);
        setError("Too many failed attempts. Form temporarily locked for 30 seconds.");
      }
      return;
    }

    try {
      setLoading(true);
      const { data } = await axios.post(
        `${API_ENDPOINT}/user/login`,
        { username: cleanUsername, password },
        getDbHeader()
      );
      saveToken(data.data.token);
      toast.success("Login successful!");
      await reloadUser();
      navigate(`/`);
    } catch (err) {
      const errMsg = getErrorResponse(err);
      setError(errMsg);
      toast.error(errMsg || "Login failed. Please check your credentials.");

      const newFailed = failedAttempts + 1;
      setFailedAttempts(newFailed);
      if (newFailed >= 5) {
        setLockCountdown(30);
        setError("Too many failed attempts. Login locked for 30 seconds.");
      }
      refreshCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const passStrength = getPasswordStrength(password);

  return (
    <Box
      sx={{
        position: "relative",
        zIndex: 0,
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: (theme) => theme.palette.background.default,
        overflow: "hidden",
      }}
    >
      <FloatingIcon Icon={Settings} top="10%" left="5%" size={120} animation={float} delay="0s" />
      <FloatingIcon Icon={Factory} top="60%" left="15%" size={150} animation={floatReverse} delay="2s" />
      <FloatingIcon Icon={Wrench} top="20%" right="10%" size={100} animation={floatReverse} delay="1s" />
      <FloatingIcon Icon={Cpu} top="70%" right="5%" size={140} animation={float} delay="3s" />
      <FloatingIcon Icon={Hammer} top="40%" left="80%" size={90} animation={float} delay="1.5s" />
      <FloatingIcon Icon={Cog} top="-5%" left="40%" size={180} animation={floatReverse} delay="0.5s" />

      <Container maxWidth="xs" sx={{ zIndex: 1, position: "relative" }}>
        <Paper
          elevation={4}
          sx={{
            p: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            borderRadius: 3,
            backgroundColor: "rgba(255, 255, 255, 0.92)",
            backdropFilter: "blur(14px)",
            boxShadow: "0 12px 40px rgba(2, 71, 95, 0.12)",
            border: "1px solid rgba(226, 232, 240, 0.8)",
          }}
        >
          <Box component="div" sx={{ width: 180, marginBottom: 2.5 }}>
            <img
              src="https://s3.ap-south-1.amazonaws.com/expoplanner.in/download.png"
              alt="Company Logo"
              width="100%"
              style={{ display: "block", margin: "0 auto" }}
            />
          </Box>

          <Chip
            icon={<ShieldCheck size={14} color="#10B981" />}
            label="256-bit Encrypted Portal"
            size="small"
            sx={{
              bgcolor: "#ECFDF5",
              color: "#047857",
              fontWeight: 600,
              fontSize: "0.75rem",
              mb: 3,
              border: "1px solid #A7F3D0",
            }}
          />

          {lockCountdown > 0 && (
            <Alert severity="warning" icon={<ShieldAlert size={20} />} sx={{ width: "100%", mb: 2 }}>
              Account temporarily locked due to multiple failed attempts. Retry in{" "}
              <strong>{lockCountdown}s</strong>.
            </Alert>
          )}

          {error && lockCountdown <= 0 && (
            <Alert severity="error" sx={{ width: "100%", mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box
            component="form"
            noValidate
            onSubmit={handleSubmit}
            autoComplete="off"
            sx={{ width: "100%" }}
          >
            {/* Username Field */}
            <TextField
              label="Username"
              variant="outlined"
              fullWidth
              required
              disabled={lockCountdown > 0}
              size="medium"
              margin="normal"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": { backgroundColor: "#ffffff" },
              }}
            />

            {/* Password Field */}
            <FormControl variant="outlined" size="medium" required fullWidth disabled={lockCountdown > 0} sx={{ mt: 1.5 }}>
              <InputLabel htmlFor="standard-adornment-password">Password</InputLabel>
              <OutlinedInput
                id="standard-adornment-password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                sx={{ backgroundColor: "#ffffff" }}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      onMouseDown={(event) => event.preventDefault()}
                      edge="end"
                    >
                      {!showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                label="Password"
              />
            </FormControl>

            {/* Password Strength Meter */}
            {password && (
              <Box sx={{ mt: 1, mb: 1 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                  <Typography variant="caption" color="textSecondary">
                    Password Security
                  </Typography>
                  <Typography variant="caption" fontWeight="600" sx={{ color: passStrength.color }}>
                    {passStrength.label}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={passStrength.score}
                  sx={{
                    height: 5,
                    borderRadius: 3,
                    bgcolor: "#E2E8F0",
                    "& .MuiLinearProgress-bar": { bgcolor: passStrength.color },
                  }}
                />
              </Box>
            )}

            {/* Captcha Security Section */}
            <Box
              sx={{
                mt: 2.5,
                p: 2,
                borderRadius: 2,
                bgcolor: "#F8FAFC",
                border: captchaError ? "1px solid #EF4444" : "1px solid #E2E8F0",
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                <Typography variant="caption" fontWeight="700" color="#334155" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Security size={14} style={{ color: "#2563EB" }} /> Security CAPTCHA
                </Typography>
                <Tooltip title="Generate New CAPTCHA">
                  <IconButton size="small" onClick={refreshCaptcha} disabled={lockCountdown > 0}>
                    <Refresh size={16} color="#64748B" />
                  </IconButton>
                </Tooltip>
              </Box>

              <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", mb: 1.5 }}>
                <canvas
                  ref={canvasRef}
                  width={160}
                  height={44}
                  style={{
                    borderRadius: "6px",
                    border: "1px solid #CBD5E1",
                    boxShadow: "inset 0 1px 3px rgba(0,0,0,0.05)",
                  }}
                />
                <Typography variant="caption" color="#64748B" sx={{ fontSize: "0.75rem", lineHeight: 1.2 }}>
                  Type the distorted characters
                </Typography>
              </Box>

              <TextField
                label="Enter CAPTCHA Code"
                variant="outlined"
                fullWidth
                required
                size="small"
                disabled={lockCountdown > 0}
                value={captchaInput}
                onChange={(e) => setCaptchaInput(e.target.value)}
                error={captchaError}
                placeholder="e.g. A8K9X2"
                sx={{
                  "& .MuiOutlinedInput-root": { backgroundColor: "#ffffff" },
                }}
              />
            </Box>

            {/* Submit Button */}
            <LoadingButton
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              disabled={lockCountdown > 0}
              sx={{ mt: 3, mb: 1, py: 1.2, borderRadius: 2, fontWeight: 700 }}
              loading={loading}
              startIcon={<Lock size={18} />}
            >
              {lockCountdown > 0 ? `Locked (${lockCountdown}s)` : "Secure Login"}
            </LoadingButton>
          </Box>
        </Paper>
      </Container>

      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          width: "100%",
          p: 2,
          textAlign: "center",
          zIndex: 1,
        }}
      >
        <Typography variant="body2" color="textSecondary">
          © {new Date().getFullYear()} Expoplanner. All Rights Reserved.
        </Typography>
      </Box>
    </Box>
  );
}

export default Login;
